/**
 * 组织架构详情 → cytoscape 元素转换工具。
 *
 * 本版布局目标：
 * - 公司根节点位于顶部居中
 * - 一级部门仍按横向分支展开
 * - 每个部门内部严格按 members 原始顺序排布
 * - 同一部门下，直属人员与子部门视为同级条目，统一纵向排列
 * - 子部门的成员再向右缩进一层，形成清晰递归结构
 */

import type {
  OrganizationDetail,
  OrganizationMemberNode,
} from '../../../../../api';
import { BRANCH_THEMES } from './processGraph';

/** 组织图中的节点类别 */
export type OrgNodeKind = 'company' | 'department' | 'person';

/** 组织图节点数据 */
export interface OrgNodeData {
  id: string;
  /** 原始名称，用于按 name 查找并更新状态 */
  originalName: string;
  label: string;
  kind: OrgNodeKind;
  /** 对部门节点，记录一个主题色，孩子节点共享 */
  themeColor: string;
  themeSoftBg: string;
  /** 对人员节点，记录 roles；公司/部门为空数组 */
  roles: string[];
  /** 第二行副标题，当前使用人员第一个角色名 */
  subtitle?: string;
  /** 运行前预估好的节点尺寸，供 cytoscape 样式直接读取 */
  nodeWidth: number;
  nodeHeight: number;
  /** 人员节点可能有来自接口的业务 id；没有就使用 cytoscape id */
  businessId?: string;
  /** 人员头像 URL（可选）；为空时渲染默认头像图标。 */
  avatarUrl?: string;
  nodeType: 'org';
  status: 'pending';
}

export interface OrganizationGraphCompileResult {
  elements: {
    group: 'nodes' | 'edges';
    data: Record<string, unknown>;
    classes?: string;
    position?: { x: number; y: number };
  }[];
  /** 按原始 name 查找节点 id（同名人员少见，仍用数组） */
  nameToIds: Map<string, string[]>;
  nodeMap: Map<string, OrgNodeData>;
  /** 所有人员节点 id（候选池基础列表） */
  personIds: string[];
}

type BranchTheme = (typeof BRANCH_THEMES)[number];

interface LayoutMetrics {
  member: OrganizationMemberNode;
  kind: OrgNodeKind;
  theme: BranchTheme;
  label: string;
  width: number;
  height: number;
  nodeWidth: number;
  nodeHeight: number;
  subtitle?: string;
  /** 直属子项严格保留 members 原始顺序，用于同级混排。 */
  children: LayoutMetrics[];
}

const COMPANY_TOP_Y = 72;
// 根节点与一级部门之间需要留出足够的垂直距离，
// 否则 taxi 折线会因空间过小退化成贴顶横线。
const ROOT_TO_BRANCH_GAP_Y = 228;
const BRANCH_GAP_X = 92;
const CHILD_INDENT_X = 128;
const CHILD_START_GAP_Y = 34;
const CHILD_STACK_GAP_Y = 22;
const GRAPH_LEFT = 40;

const COMPANY_MIN_WIDTH = 320;
const COMPANY_MAX_WIDTH = 440;
const DEPARTMENT_MIN_WIDTH = 220;
const DEPARTMENT_MAX_WIDTH = 280;
const PERSON_CARD_WIDTH = 236;

const COMPANY_BASE_HEIGHT = 88;
const DEPARTMENT_HEIGHT = 78;
const PERSON_HEIGHT = 84;

/**
 * 编译组织树到 cytoscape 元素。
 *
 * 兼容策略：
 * - 新数据优先使用显式 type 区分 department / person
 * - 旧数据没有 type 时，继续按“有 members 或无 roles => department”兜底
 */
export function compileOrganizationGraph(
  detail: OrganizationDetail
): OrganizationGraphCompileResult {
  const elements: OrganizationGraphCompileResult['elements'] = [];
  const nameToIds = new Map<string, string[]>();
  const nodeMap = new Map<string, OrgNodeData>();
  const personIds: string[] = [];

  let counter = 0;
  const genId = (prefix: string) => {
    counter += 1;
    return `${prefix}_${counter}`;
  };

  const recordNode = (
    data: OrgNodeData,
    classes: string,
    position: { x: number; y: number }
  ) => {
    nodeMap.set(data.id, data);
    const existing = nameToIds.get(data.originalName) || [];
    existing.push(data.id);
    nameToIds.set(data.originalName, existing);
    elements.push({
      group: 'nodes',
      data: { ...data },
      classes,
      position,
    });
  };

  const recordEdge = (
    source: string,
    target: string,
    theme: BranchTheme,
    edgeClass: string
  ) => {
    elements.push({
      group: 'edges',
      data: {
        id: `e_${source}__${target}`,
        source,
        target,
        edgeKind: 'org',
        themeColor: theme.color,
      },
      classes: `org-edge ${edgeClass} theme-${theme.id}`,
    });
  };

  const buildMetrics = (
    member: OrganizationMemberNode,
    theme: BranchTheme,
    kind: OrgNodeKind
  ): LayoutMetrics => {
    if (kind === 'person') {
      const subtitle = getPrimaryRole(member);
      return {
        member,
        kind,
        theme,
        label: member.name,
        width: PERSON_CARD_WIDTH,
        height: PERSON_HEIGHT,
        nodeWidth: PERSON_CARD_WIDTH,
        nodeHeight: PERSON_HEIGHT,
        subtitle,
        children: [],
      };
    }

    const children = (member.members || []).map(child =>
      buildMetrics(child, theme, resolveMemberKind(child))
    );
    const label = kind === 'company' ? buildCompanyLabel(member) : member.name;
    const nodeWidth =
      kind === 'company'
        ? estimateCompanyWidth(label)
        : estimateDepartmentWidth(member.name);
    const nodeHeight =
      kind === 'company' ? estimateCompanyHeight(label) : DEPARTMENT_HEIGHT;

    if (children.length === 0) {
      return {
        member,
        kind,
        theme,
        label,
        width: nodeWidth,
        height: nodeHeight,
        nodeWidth,
        nodeHeight,
        children,
      };
    }

    const childMaxWidth = Math.max(...children.map(child => child.width));
    const childStackHeight = children.reduce(
      (sum, child, index) =>
        sum + child.height + (index === 0 ? 0 : CHILD_STACK_GAP_Y),
      0
    );

    return {
      member,
      kind,
      theme,
      label,
      width: Math.max(nodeWidth, CHILD_INDENT_X + childMaxWidth),
      height: Math.max(
        nodeHeight,
        nodeHeight + CHILD_START_GAP_Y + childStackHeight
      ),
      nodeWidth,
      nodeHeight,
      children,
    };
  };

  const placeMember = (
    metrics: LayoutMetrics,
    parentId: string | null,
    branchLeft: number,
    topY: number,
    incomingEdgeClass = 'dept-edge'
  ) => {
    const isCompany = metrics.kind === 'company';
    const isDepartment = metrics.kind === 'department';
    const id = genId(isCompany ? 'company' : isDepartment ? 'dept' : 'person');
    const position = {
      x: branchLeft + metrics.nodeWidth / 2,
      y: topY + metrics.nodeHeight / 2,
    };
    const subtitle = metrics.subtitle;
    const label =
      metrics.kind === 'person' && subtitle
        ? `${metrics.member.name}\n${subtitle}`
        : metrics.label;
    const data: OrgNodeData = {
      id,
      originalName: metrics.member.name,
      label,
      kind: metrics.kind,
      themeColor: metrics.kind === 'company' ? '#111111' : metrics.theme.color,
      themeSoftBg:
        metrics.kind === 'company' ? '#e5e7eb' : metrics.theme.softBg,
      roles: metrics.member.roles || [],
      subtitle,
      nodeWidth: metrics.nodeWidth,
      nodeHeight: metrics.nodeHeight,
      businessId: metrics.member.id,
      avatarUrl:
        metrics.kind === 'person' ? (metrics.member.avatar || '').trim() : '',
      nodeType: 'org',
      status: 'pending',
    };

    recordNode(
      data,
      `org-node ${metrics.kind} theme-${metrics.theme.id} status-pending`,
      position
    );
    if (metrics.kind === 'person') {
      personIds.push(id);
    }
    if (parentId) {
      recordEdge(parentId, id, metrics.theme, incomingEdgeClass);
    }

    if (metrics.kind === 'person' || metrics.children.length === 0) return id;

    let childTopY = topY + metrics.nodeHeight + CHILD_START_GAP_Y;
    const childLeft = branchLeft + CHILD_INDENT_X;

    metrics.children.forEach(child => {
      placeMember(
        child,
        id,
        childLeft,
        childTopY,
        child.kind === 'person' ? 'person-edge' : 'dept-edge'
      );
      childTopY += child.height + CHILD_STACK_GAP_Y;
    });

    return id;
  };

  const company = detail.organization;
  const rootTheme = BRANCH_THEMES[0];
  const rootLabel = buildCompanyLabel(company);
  const rootMetrics: LayoutMetrics = {
    member: company,
    kind: 'company',
    theme: rootTheme,
    label: rootLabel,
    width: estimateCompanyWidth(rootLabel),
    height: estimateCompanyHeight(rootLabel),
    nodeWidth: estimateCompanyWidth(rootLabel),
    nodeHeight: estimateCompanyHeight(rootLabel),
    children: [],
  };
  const topBranches = (company.members || []).map((member, index) =>
    buildMetrics(
      member,
      BRANCH_THEMES[index % BRANCH_THEMES.length],
      resolveMemberKind(member)
    )
  );
  const totalBranchesWidth =
    topBranches.reduce((sum, branch) => sum + branch.width, 0) +
    Math.max(0, topBranches.length - 1) * BRANCH_GAP_X;
  const rootLeft =
    GRAPH_LEFT + Math.max(0, (totalBranchesWidth - rootMetrics.nodeWidth) / 2);
  const rootId = placeMember(rootMetrics, null, rootLeft, COMPANY_TOP_Y);

  let branchLeft = GRAPH_LEFT;
  topBranches.forEach(branch => {
    placeMember(
      branch,
      rootId,
      branchLeft,
      COMPANY_TOP_Y + ROOT_TO_BRANCH_GAP_Y,
      'root-edge'
    );
    branchLeft += branch.width + BRANCH_GAP_X;
  });

  return { elements, nameToIds, nodeMap, personIds };
}

function resolveMemberKind(member: OrganizationMemberNode): OrgNodeKind {
  if (member.type === 'department') return 'department';
  if (member.type === 'person') return 'person';
  return isDepartmentMember(member) ? 'department' : 'person';
}

function isDepartmentMember(member: OrganizationMemberNode): boolean {
  return !!((member.members && member.members.length) || !member.roles);
}

function getPrimaryRole(member: OrganizationMemberNode): string {
  return (member.roles || []).find(Boolean) || '';
}

/**
 * 公司名称与 owner 用单个换行组成主副两行：
 * - 第一行保留公司名
 * - 第二行显示“姓名（角色）”
 */
function buildCompanyLabel(member: OrganizationMemberNode): string {
  const ownerName = member.owner?.name?.trim();
  if (!ownerName) return member.name;

  const primaryRole = (member.owner?.roles || []).find(Boolean);
  const ownerLine = primaryRole ? `${ownerName}（${primaryRole}）` : ownerName;

  return `${member.name}\n${ownerLine}`;
}

/** 中文按 12px、英文按 7px 估算文本宽度，足够用于卡片宽度估算。 */
function estimateTextWidth(text: string): number {
  let width = 0;
  for (const ch of text) {
    if (ch === '\n') continue;
    width += /[\u4e00-\u9fa5]/.test(ch) ? 12 : 7;
  }
  return width;
}

function getTextLines(text: string): string[] {
  const lines = text.split('\n').map(line => line.trim());
  return lines.length > 0 ? lines : [''];
}

function estimateCompanyWidth(name: string): number {
  const maxLineWidth = Math.max(...getTextLines(name).map(estimateTextWidth));
  return clamp(
    Math.ceil(maxLineWidth + 120),
    COMPANY_MIN_WIDTH,
    COMPANY_MAX_WIDTH
  );
}

function estimateCompanyHeight(name: string): number {
  const lineCount = getTextLines(name).length;
  return COMPANY_BASE_HEIGHT + Math.max(0, lineCount - 1) * 18;
}

function estimateDepartmentWidth(name: string): number {
  const maxLineWidth = Math.max(...getTextLines(name).map(estimateTextWidth));
  return clamp(
    Math.ceil(maxLineWidth + 84),
    DEPARTMENT_MIN_WIDTH,
    DEPARTMENT_MAX_WIDTH
  );
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
